import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const CONSENT_KEY = "ptm_cookie_consent_v2";

interface Consent {
  necessary: boolean;
  analytics: boolean;
  functional: boolean;
}

const defaultConsent: Consent = {
  necessary: true,
  analytics: false,
  functional: false,
};

function getStoredConsent(): Consent | null {
  try {
    const raw = localStorage.getItem(CONSENT_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function hasConsentFor(category: keyof Consent): boolean {
  const c = getStoredConsent();
  if (!c) return category === "necessary";
  return c[category] ?? false;
}

export default function CookieBanner() {
  const [visible, setVisible] = useState(false);
  const [configOpen, setConfigOpen] = useState(false);
  const [prefs, setPrefs] = useState<Consent>(defaultConsent);

  useEffect(() => {
    const stored = getStoredConsent();
    if (!stored) {
      setVisible(true);
    }
  }, []);

  const saveConsent = (consent: Consent) => {
    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setVisible(false);
  };

  const handleAcceptAll = () => {
    saveConsent({ necessary: true, analytics: true, functional: true });
  };

  const handleReject = () => {
    saveConsent({ necessary: true, analytics: false, functional: false });
  };

  const handleSaveConfig = () => {
    saveConsent(prefs);
  };

  if (!visible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-[9997] bg-white border-t-2 border-primary shadow-[0_-4px_20px_rgba(0,0,0,0.08)]">
      <div className="max-w-container mx-auto px-4 md:px-6 py-5">
        {/* Mensaje principal */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 mb-4">
          <div className="flex-1">
            <p className="font-body text-sm text-text-primary leading-relaxed">
              Utilizamos cookies propias y de terceros para mejorar su experiencia y analizar el tráfico de este sitio web. Puede gestionar sus preferencias o aceptar todas las cookies. Para más información, consulte nuestra{" "}
              <Link to="/politica-cookies" className="underline text-secondary hover:text-secondary-dark transition-colors">
                Política de Cookies
              </Link>.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 flex-wrap justify-center">
            <button
              onClick={handleReject}
              className="font-body font-semibold text-sm px-5 py-3 border-2 border-primary text-primary rounded-pill hover:bg-primary/5 transition-colors whitespace-nowrap"
            >
              Rechazar
            </button>
            <button
              onClick={() => setConfigOpen(!configOpen)}
              className="font-body font-semibold text-sm px-5 py-3 border-2 border-primary text-primary rounded-pill hover:bg-primary/5 transition-colors whitespace-nowrap"
            >
              {configOpen ? "Cerrar configuración" : "Configurar"}
            </button>
            <button
              onClick={handleAcceptAll}
              className="font-body font-semibold text-sm px-5 py-3 bg-primary hover:bg-primary-dark text-white rounded-pill transition-colors whitespace-nowrap"
            >
              Aceptar
            </button>
          </div>
        </div>

        {/* Panel de configuración */}
        {configOpen && (
          <div className="border-t border-border pt-4 mt-2">
            <h4 className="font-body font-semibold text-sm text-text-primary mb-3">
              Gestionar cookies por categoría
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              {/* Necesarias */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <div className="font-body font-semibold text-sm text-text-primary">Técnicas / Esenciales</div>
                  <div className="font-body text-xs text-text-secondary mt-0.5">
                    Necesarias para el funcionamiento del sitio. No se pueden desactivar.
                  </div>
                </div>
                <div className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-primary rounded-full peer peer-disabled:opacity-50"></div>
                  <div className="absolute left-0.5 top-0.5 bg-white w-5 h-5 rounded-full transition-transform translate-x-5"></div>
                </div>
              </div>

              {/* Analíticas */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <div className="font-body font-semibold text-sm text-text-primary">Analíticas</div>
                  <div className="font-body text-xs text-text-secondary mt-0.5">
                    Google Analytics 4. Medimos el tráfico para mejorar el sitio.
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={prefs.analytics}
                    onChange={(e) => setPrefs({ ...prefs, analytics: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>

              {/* Funcionales */}
              <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
                <div>
                  <div className="font-body font-semibold text-sm text-text-primary">Funcionales</div>
                  <div className="font-body text-xs text-text-secondary mt-0.5">
                    Preferencias de idioma, mapas interactivos, WhatsApp.
                  </div>
                </div>
                <label className="relative inline-flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={prefs.functional}
                    onChange={(e) => setPrefs({ ...prefs, functional: e.target.checked })}
                    className="sr-only peer"
                  />
                  <div className="w-11 h-6 bg-border peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
                </label>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handleSaveConfig}
                className="font-body font-semibold text-sm px-6 py-2.5 bg-text-primary hover:bg-slate-800 text-white rounded-pill transition-colors whitespace-nowrap"
              >
                Guardar preferencias
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}